import jwt_decode from 'jwt-decode';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useReducer, useState } from 'react';
import { modules } from 'src/modules';

import authApi from '../api/auth';
import userApi from '../api/user';
import useInterval from '../hooks/use-interval';
import { useLocation, useNavigate } from 'react-router-dom';
import { Creators as UserCreators } from 'src/store/ducks/user';
import { useNotification } from './notification-context';
import { triggerSessionExpiredModal } from 'src/services/sessionExpired';

const STORAGE_KEY = 'accessToken';
const SESSION_KEY = 'weesu_session_id';
const authChannel = new BroadcastChannel('weesu-auth');

const safePostMessage = (data) => {
  try {
    authChannel.postMessage(data);
  } catch {}
};

var ActionType;
(function (ActionType) {
  ActionType['INITIALIZE'] = 'INITIALIZE';
  ActionType['SIGN_IN'] = 'SIGN_IN';
  ActionType['SIGN_UP'] = 'SIGN_UP';
  ActionType['SIGN_OUT'] = 'SIGN_OUT';
  ActionType['PERMISSIONS'] = 'PERMISSIONS';
  ActionType['PARAMETERS'] = 'PARAMETERS';
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  permissions: null,
  parameters: null,
  isAdmin: false,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, permissions, parameters } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      permissions,
      parameters,
    };
  },
  SIGN_IN: (state, action) => {
    const { user, permissions, parameters } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      permissions,
      parameters,
    };
  },
  SIGN_UP: (state, action) => {
    const { user, permissions, parameters } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      permissions,
      parameters,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    permissions: null,
    parameters: null,
  }),
  PERMISSIONS: (state, action) => {
    return {
      ...state,
      permissions: action.payload,
    };
  },
  PARAMETERS: (state, action) => {
    return {
      ...state,
      parameters: action.payload,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

export const AuthContext = createContext({
  ...initialState,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
});

const createSessionId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const AuthProvider = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [tabSessionId, setTabSessionId] = useState('');
  const [suppressModal, setSuppressModal] = useState(false);
  const { setNotifications } = useNotification();

  // carrega sessionId desta aba, se existir
  useEffect(() => {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) {
      setTabSessionId(existing);
    }
  }, []);

  const initialize = useCallback(async () => {
    try {
      const accessToken = window.localStorage.getItem(STORAGE_KEY);
      if (accessToken && !['/login', '/', '/transportadora'].includes(window.location.pathname)) {
        const { user, token } = await authApi.getAuthenticated(accessToken);
        if (!user) {
          window.localStorage.removeItem(STORAGE_KEY);
          window.localStorage.removeItem(SESSION_KEY);
          window.localStorage.removeItem('companyName');
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
              permissions: null,
              parameters: null,
            },
          });
          navigate('/login', { replace: true });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          window.localStorage.setItem(STORAGE_KEY, token);
          const response = await UserCreators.getCachedUserData();
          if (response && response.communicates) {
            setNotifications(
              response.communicates.map((n, index) => ({
                ...n,
                id: index,
                hidden: n.hidden ?? false,
              }))
            );
          }
        }
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
            permissions: null,
            parameters: null,
          },
        });
      }
    } catch (err) {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(SESSION_KEY);
      window.localStorage.removeItem('companyName');
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
          permissions: null,
          parameters: null,
        },
      });
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate, setNotifications]);

  const refreshToken = async () => {
    const accessToken = window.localStorage.getItem(STORAGE_KEY);
    if (accessToken) {
      const { token } = await authApi.getAuthenticated(accessToken);
      if (token) {
        window.localStorage.setItem(STORAGE_KEY, token);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(SESSION_KEY);
        window.localStorage.removeItem('companyName');
        navigate('/login', { replace: true });
      }
    }
  };

  // BroadcastChannel: sincroniza LOGIN/LOGOUT entre abas via sessionId
  useEffect(() => {
    const handleAuthMessage = (event) => {
      if (!event?.data || !event.data.type) return;

      if (event.data.type === 'LOGOUT') {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem('companyName');
        window.localStorage.removeItem(SESSION_KEY);
        dispatch({ type: ActionType.SIGN_OUT });

        if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
          navigate('/', { replace: true });
        }
        return;
      }

      if (event.data.type === 'LOGIN') {
        const { sessionId: newSessionId } = event.data;

        // Aba ainda sem sessão registrada → ignora
        if (!tabSessionId) {
          return;
        }

        // Mesma sessão desta aba → ignora (é a aba que acabou de logar)
        if (tabSessionId === newSessionId) {
          return;
        }

        // Sessão diferente → derruba esta aba
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(SESSION_KEY);
        dispatch({ type: ActionType.SIGN_OUT });
        navigate('/login', { replace: true });
      }
    };

    authChannel.addEventListener('message', handleAuthMessage);

    return () => {
      authChannel.removeEventListener('message', handleAuthMessage);
    };
  }, [dispatch, navigate, tabSessionId]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const signIn = useCallback(
    async (email, password) => {
      try {
        window.__SUPPRESS_SESSION_MODAL__ = true;
        const res = await authApi.signIn(email, password);
        const { user, token, userCheckedLastVersion, communicates } = res;

        if (_.isEmpty(user)) {
          throw new Error('Usuário ou senha inválidos');
        }

        window.localStorage.setItem('user_last_checked_version', userCheckedLastVersion === true ? 'true' : 'false');
        window.localStorage.setItem('companyName', user?.tenant_alias);
        window.localStorage.setItem(STORAGE_KEY, token);

        // cria sessionId desta aba
        const newSessionId = createSessionId();
        window.localStorage.setItem(SESSION_KEY, newSessionId);
        setTabSessionId(newSessionId);

        dispatch({
          type: ActionType.SIGN_IN,
          payload: {
            user,
          },
        });

        // avisa outras abas qual sessão ficou ativa
        safePostMessage({ type: 'LOGIN', sessionId: newSessionId });

        if (communicates && communicates.length > 0) {
          setNotifications(
            communicates.map((n, index) => ({
              ...n,
              id: index,
              hidden: n.hidden ?? false,
            }))
          );
        }
      } finally {
        setTimeout(() => {
          window.__SUPPRESS_SESSION_MODAL__ = false;
        }, 2000);
      }
    },
    [dispatch, setNotifications]
  );

  const signUp = useCallback(
    async (payload) => {
      const { user, token, communicates } = await authApi.signUp({
        ...payload,
      });
      window.localStorage.setItem(STORAGE_KEY, token);

      const newSessionId = createSessionId();
      window.localStorage.setItem(SESSION_KEY, newSessionId);
      setTabSessionId(newSessionId);

      window.localStorage.setItem('companyName', user?.tenant_alias);

      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });

      safePostMessage({ type: 'LOGIN', sessionId: newSessionId });

      if (communicates && communicates.length > 0) {
        setNotifications(
          communicates.map((n, index) => ({
            ...n,
            id: index,
            hidden: n.hidden ?? false,
          }))
        );
      }
    },
    [dispatch, setNotifications]
  );

  const signOut = useCallback(
    async (redirect = true) => {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem('companyName');
      window.localStorage.removeItem(SESSION_KEY);
      setTabSessionId(null);

      safePostMessage({ type: 'LOGOUT' });

      dispatch({ type: ActionType.SIGN_OUT });

      if (redirect) navigate('/', { replace: true });
      else triggerSessionExpiredModal();
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (state.permissions?.id) {
      const moduleId =
        Object.values(modules).find((module) => {
          if (module.regex) {
            const newRegExp = new RegExp(module.path);
            return newRegExp.test(location.pathname);
          } else {
            return module.path === location.pathname;
          }
        }) || {};

      if (moduleId?.id) {
        userApi
          .getParameters(moduleId.params)
          .then((data) => {
            dispatch({
              type: ActionType.PARAMETERS,
              payload: data,
            });
          })
          .catch(() => {
            dispatch({
              type: ActionType.PARAMETERS,
              payload: null,
            });
          });
      }
    }
  }, [state.permissions, location.pathname]);

  useEffect(() => {
    const moduleId =
      Object.values(modules).find((module) => {
        if (module.regex) {
          return new RegExp(module.path).test(location.pathname);
        } else {
          return module.path === location.pathname;
        }
      }) || {};

    if (moduleId?.id) {
      userApi
        .getPermission(moduleId.id)
        .then((data) => {
          dispatch({
            type: ActionType.PERMISSIONS,
            payload: data,
          });
        })
        .catch(() => {
          dispatch({
            type: ActionType.PERMISSIONS,
            payload: null,
          });
          triggerSessionExpiredModal();
        });
    } else {
      dispatch({
        type: ActionType.PERMISSIONS,
        payload: null,
      });
    }
  }, [location.pathname, state.user]);

  useInterval(() => {
    checkTokenExpiration();
  }, 30000);

  const checkTokenExpiration = () => {
    const token = window.localStorage.getItem(STORAGE_KEY);
    if (!token) {
      return;
    }

    try {
      const decoded = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiration = decoded.exp - currentTime;

      if (timeUntilExpiration <= 0) {
        signOut(false);
      } else if (timeUntilExpiration <= 60) {
        refreshToken();
      }
    } catch (error) {
      signOut();
    }
  };

  const isAdminEmail = (email) => {
    if (!email) return false;
    return /^weesu\.core\.[^@]+@weesu\.com\.br$/.test(email);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        signOut,
        isAdmin: isAdminEmail(state.user?.email),
      }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
